import React, { useCallback, useEffect, useState } from "react";
import { Inbox, Mail, Phone, Briefcase, Building2 } from "lucide-react";
import { toast } from "sonner";
import { fetchAllRequests, updateRequestStatus, convertRequest } from "../../api";
import { RequestTimeline } from "./RequestTimeline";
import { AttachmentsBlock } from "./AttachmentsBlock";

const STATUSES = ["submitted", "reviewing", "in_progress", "completed", "cancelled"];

const ENTITY_TYPES = [
  { value: "1040", label: "Schedule C (Form 1040)" },
  { value: "1065", label: "Partnership (Form 1065)" },
  { value: "1120S", label: "S-Corp (Form 1120-S)" },
  { value: "1120", label: "C-Corp (Form 1120)" },
];

const ConvertDialog = ({ pending, busy, onCancel, onConfirm }) => {
  const [name, setName] = useState("");
  const [entityType, setEntityType] = useState("1040");
  useEffect(() => {
    if (pending) {
      setName(pending.customer.full_name || "");
      setEntityType("1040");
    }
  }, [pending]);
  if (!pending) return null;
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="convert-dialog">
      <div className="glass-panel rounded-md shadow-2xl max-w-sm w-full p-6 border border-white/10 space-y-4 bg-zinc-900/95">
        <div className="flex items-center space-x-2">
          <Briefcase className="w-5 h-5 text-blue-400" />
          <h3 className="font-bold text-sm text-white">Convert to Client Engagement</h3>
        </div>
        <p className="text-xs text-zinc-400 leading-relaxed">
          Creates a full engagement for <span className="font-mono text-zinc-300">{pending.customer.email}</span> — it
          appears in Firm Overview and the entity switcher, starting in AWAITING_DOCUMENTS.
        </p>
        <input
          data-testid="convert-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Legal / entity name"
          className="w-full bg-black border border-white/20 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500"
        />
        <select
          data-testid="convert-entity-select"
          value={entityType}
          onChange={(e) => setEntityType(e.target.value)}
          className="w-full bg-black border border-white/20 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:border-blue-500 cursor-pointer"
        >
          {ENTITY_TYPES.map((t) => (
            <option key={t.value} value={t.value}>{t.label}</option>
          ))}
        </select>
        <div className="flex items-center space-x-2">
          <button data-testid="convert-cancel-btn" onClick={onCancel} disabled={busy} className="flex-1 px-3 py-2 bg-zinc-900 hover:bg-white/5 border border-white/10 text-zinc-300 text-xs font-semibold rounded-md transition disabled:opacity-50">
            Cancel
          </button>
          <button
            data-testid="convert-confirm-btn"
            onClick={() => onConfirm(name, entityType)}
            disabled={busy || name.trim().length < 3}
            className="flex-1 px-3 py-2 bg-white hover:bg-zinc-200 text-black text-xs font-bold rounded-md transition-colors disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create Engagement"}
          </button>
        </div>
      </div>
    </div>
  );
};

const STATUS_STYLES = {
  submitted: "bg-blue-500/10 text-blue-300 border-blue-500/40",
  reviewing: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  in_progress: "bg-indigo-500/10 text-indigo-300 border-indigo-500/30",
  completed: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  cancelled: "bg-rose-500/10 text-rose-400 border-rose-500/30",
};

const NEXT_STEP = { submitted: "reviewing", reviewing: "in_progress", in_progress: "completed" };
const NEXT_LABEL = { submitted: "Start Review", reviewing: "Start Work", in_progress: "Mark Completed" };

const RequestsView = ({ onConverted }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [convertPending, setConvertPending] = useState(null);
  const [convertBusy, setConvertBusy] = useState(false);

  const load = useCallback(() => {
    fetchAllRequests().then(setData).catch(() => setError(true));
  }, []);

  useEffect(() => { load(); }, [load]);

  const doConvert = async (name, entityType) => {
    setConvertBusy(true);
    try {
      const res = await convertRequest(convertPending.id, name.trim(), entityType);
      toast.success(`${res.name} is now a client engagement`, {
        description: "Find it in Firm Overview and the entity switcher.",
      });
      setConvertPending(null);
      load();
      if (onConverted) onConverted();
    } catch (e) {
      toast.error("Conversion failed", {
        description: (e.response && e.response.data && e.response.data.detail) || "Request failed",
      });
    }
    setConvertBusy(false);
  };

  const setStatus = async (id, status) => {
    setBusyId(id);
    try {
      const res = await updateRequestStatus(id, status);
      toast.success(`Request moved to ${status.replace(/_/g, " ")}`, {
        description: res.email_sent
          ? "Customer notified by email."
          : "Customer email notice could not be delivered (Resend test mode).",
      });
      load();
    } catch (e) {
      toast.error("Status update failed", {
        description: (e.response && e.response.data && e.response.data.detail) || "Request failed",
      });
    }
    setBusyId(null);
  };

  if (error)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-rose-400" data-testid="view-requests">
        Failed to load service requests.
      </div>
    );
  if (!data)
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-zinc-400 animate-pulse" data-testid="view-requests">
        Loading service requests…
      </div>
    );

  const rows = filter === "all" ? data.requests : data.requests.filter((r) => r.status === filter);

  return (
    <div className="flex-1 overflow-y-auto custom-scrollbar p-8" data-testid="view-requests">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="border-b border-white/10 pb-4">
          <h1 className="text-2xl font-black text-white">Service Requests</h1>
          <p className="text-zinc-400 text-xs mt-1">
            Every request submitted through the intake form — move each one through its lifecycle.
          </p>
        </div>

        <div className="flex items-center flex-wrap gap-2" data-testid="requests-filter-bar">
          <button
            data-testid="filter-all"
            onClick={() => setFilter("all")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition ${
              filter === "all" ? "bg-white text-black border-white" : "bg-transparent text-zinc-400 border-white/10 hover:text-white"
            }`}
          >
            All ({data.total})
          </button>
          {STATUSES.map((s) => (
            <button
              key={s}
              data-testid={`filter-${s}`}
              onClick={() => setFilter(s)}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition capitalize ${
                filter === s ? "bg-white text-black border-white" : "bg-transparent text-zinc-400 border-white/10 hover:text-white"
              }`}
            >
              {s.replace(/_/g, " ")} ({data.counts[s] || 0})
            </button>
          ))}
        </div>

        {rows.length === 0 ? (
          <div className="glass-card rounded-md border border-white/10 p-12 text-center space-y-3" data-testid="requests-empty-state">
            <Inbox className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="text-sm text-zinc-400">No service requests{filter !== "all" ? ` in "${filter.replace(/_/g, " ")}"` : " yet"}.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="staff-requests-list">
            {rows.map((r) => (
              <div key={r.id} className="glass-card rounded-md border border-white/10 p-5 space-y-3" data-testid={`staff-request-${r.id}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center space-x-3">
                    <span className="font-mono text-[11px] text-zinc-500">{r.id}</span>
                    <span className="text-sm font-bold text-white capitalize">{r.service_type.replace(/_/g, " ")}</span>
                    <span
                      data-testid={`staff-status-${r.id}`}
                      className={`px-2.5 py-0.5 rounded-full text-[10px] font-bold border uppercase ${STATUS_STYLES[r.status]}`}
                    >
                      {r.status.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {r.engagement_slug ? (
                      <span
                        data-testid={`converted-chip-${r.id}`}
                        className="px-2.5 py-1.5 rounded-md text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 flex items-center space-x-1.5"
                        title={r.engagement_name}
                      >
                        <Building2 className="w-3 h-3" />
                        <span>Client: {r.engagement_name || r.engagement_slug}</span>
                      </span>
                    ) : (
                      <button
                        data-testid={`convert-btn-${r.id}`}
                        onClick={() => setConvertPending(r)}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-[11px] font-bold rounded-md transition-colors flex items-center space-x-1.5"
                      >
                        <Briefcase className="w-3 h-3" />
                        <span>Convert to Client</span>
                      </button>
                    )}
                    {NEXT_STEP[r.status] && (
                      <button
                        data-testid={`advance-btn-${r.id}`}
                        disabled={busyId === r.id}
                        onClick={() => setStatus(r.id, NEXT_STEP[r.status])}
                        className="px-3 py-1.5 bg-white hover:bg-zinc-200 disabled:opacity-50 text-black text-[11px] font-bold rounded-md transition-colors"
                      >
                        {NEXT_LABEL[r.status]}
                      </button>
                    )}
                    <select
                      data-testid={`status-select-${r.id}`}
                      value={r.status}
                      disabled={busyId === r.id}
                      onChange={(e) => setStatus(r.id, e.target.value)}
                      className="bg-zinc-900 border border-white/20 rounded-md px-2 py-1.5 text-[11px] text-zinc-300 focus:outline-none focus:border-blue-500 cursor-pointer capitalize"
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <p className="text-xs text-zinc-400 leading-relaxed">{r.description}</p>

                <RequestTimeline request={r} />

                <AttachmentsBlock request={r} canUpload={false} canProcess={!!r.engagement_slug} onChanged={load} />

                <div className="flex items-center flex-wrap gap-x-5 gap-y-1 text-[11px] text-zinc-500 pt-2 border-t border-white/5">
                  <span className="font-semibold text-zinc-300">{r.customer.full_name || "—"}</span>
                  <span className="flex items-center space-x-1"><Mail className="w-3 h-3" /><span className="font-mono">{r.customer.email}</span></span>
                  {r.customer.phone_number && (
                    <span className="flex items-center space-x-1"><Phone className="w-3 h-3" /><span className="font-mono">{r.customer.phone_number}</span></span>
                  )}
                  <span className="font-mono ml-auto">
                    Submitted {new Date(r.created_at).toLocaleString()}
                    {r.updated_at ? ` · Updated ${new Date(r.updated_at).toLocaleString()}` : ""}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <ConvertDialog
        pending={convertPending}
        busy={convertBusy}
        onCancel={() => setConvertPending(null)}
        onConfirm={doConvert}
      />
    </div>
  );
};

export default RequestsView;
